import type { BadgeVariant, CartDiscountType, IconName } from "@telia/teddy";
import { OldSubscription } from "./common";
import { EsimType } from "./esim";
import { InsuranceID } from "./insurance";
import { OfferingCode } from "./offeringCodes";
import { AdditionalProduct } from "./rs-data";
import { RankedSimCardType } from "./simCardTypes";
import {
    CustomerType,
    DeliveryType,
    FinancingCode,
    FinancingDuration,
    FinancingType,
    MaybeEmpty,
    PartialBy,
    DiscountType,
    ChangeType,
    WithRequired,
} from "../constants";

// TODO: ~duplicate
export const CART_ITEM_TYPE = {
    ACCESSORY: "ACCESSORY",
    BREAKAGE_FEE: "BREAKAGE_FEE",
    CONNECTED_DEVICE: "CONNECTED_DEVICE",
    DELIVERY: "DELIVERY",
    HANDSET: "HANDSET",
    "HANDSET-PREOWNED": "HANDSET-PREOWNED",
    IN_SHOP_SERVICE: "IN_SHOP_SERVICE",
    MODEM: "MODEM",
    NEW_NUMBER: "NEW_NUMBER",
    RESWITCH_EXISTING_HARDWARE: "RESWITCH_EXISTING_HARDWARE",
    RESWITCH_PLACEHOLDER: "RESWITCH_PLACEHOLDER",
    SERVICE: "SERVICE",
    ESIM: "ESIM",
    PREPAID_REFILL: "PREPAID_REFILL",
    SIM: "SIM",
    SPECIALPRODUCT: "SPECIALPRODUCT",
    SUBSCRIPTION: "SUBSCRIPTION",
    SUBSCRIPTION_DRAFT: "SUBSCRIPTION_DRAFT",
    SWITCH_BREAKAGE_FEE: "SWITCH_BREAKAGE_FEE",
    SWITCH_REMAINING_COST: "SWITCH_REMAINING_COST",
    TABLET: "TABLET",
    TRANSFER: "TRANSFER",
    VOUCHER: "VOUCHER",
    WEARABLE: "WEARABLE",
    WEBDEAL: "WEBDEAL",
    MOBILE_REFUND: "MOBILE_REFUND",
};

export type ICartType = keyof typeof CART_ITEM_TYPE;
export type ICartSubscriptionType = "SUBSCRIPTION" | "SUBSCRIPTION_DRAFT";

export interface ICartSize {
    amount: number;
    unit: string;
}

export interface ICartFinancing {
    code?: FinancingCode;
    duration?: FinancingDuration;
    hardwareItemRefId?: string;
    type?: FinancingType;
}

export interface ICartSubscriptionPhoneNumberCheck {
    isAllowedToProceed: boolean;
    type: CustomerType;
}

export interface ICartSubscriptionPhoneNumber {
    accountId?: string;
    check: ICartSubscriptionPhoneNumberCheck;
    selectedPhoneNumber: string;
    transferSubscriptionFromOtherOwner?: boolean;
}

export interface ICartSubscriptionProductUser {
    birthDate: string;
    city: string;
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber?: string;
    postcode: string;
    street: string;
}

export interface ICartSubscriptionReswitch {
    ban: string;
    breakageFee?: number;
    handset?: ISubscriptionReswitchHandset;
    imei: string;
    initiatedBy: string;
    missingExistingSwitchHandset: boolean;
    ordererPhoneNumber?: string;
    phoneNumber: string;
    remainingCost?: number;
    showSubscriptionListOnPdp?: boolean;
    totalRemainingMonths?: number;
}

interface ISubscriptionReswitchHandset {
    displayName: string;
    imageTitle?: string;
    imageUrl?: string;
    imei: string;
    sku: string;
}

export type ICartSubscriptionExisting = Pick<
    ICartSubscription,
    | "isDataUnlimited"
    | "maxAgeAllowed"
    | "name"
    | "price"
    | "size"
    | "additionalProducts"
    | "url"
    | "lineThrough"
    | "code"
    | "firstYearDiscountAmount"
>;

interface ICartSubscriptionPriceMonthly {
    amountBeforeDiscountSubscription?: number | null;
    amountDiscount?: number;
    amountSubscription: number;
    infoHtmlSubscription?: number | null;
}

interface ICartSubscriptionPriceSpecial {
    amountSubscription: number;
}

export interface ICartSubscriptionPrice {
    monthly?: ICartSubscriptionPriceMonthly;
    monthlyLines?: ICartSubscriptionPriceMonthly;
    special?: ICartSubscriptionPriceSpecial;
}

export interface ChangedService {
    newProduct: ProductService;
    oldProduct: ProductService;
}

export interface ChangedExtraSim {
    productCode: string;
    isEsim: boolean;
    existingSimNumber?: string;
}

interface ProductService {
    code: string;
    name: string;
    price: ProductServicePrice;
    shortDescription: string;
    shortName: string;
}

export interface ProductServicePrice {
    price: number;
    priceWithoutVat: number;
}

export interface ICartSubscription {
    additionalProducts?: AdditionalProduct;
    changeType?: ChangeType;
    changedServices?: ChangedService[];
    code?: OfferingCode;
    esimType?: EsimType;
    existing?: MaybeEmpty<ICartSubscriptionExisting>;
    financing?: ICartFinancing;
    firstYearDiscountAmount?: number;
    id: string;
    isDataUnlimited?: boolean;
    isExistingPrepaidKBDSubscription?: boolean;
    isLegal?: boolean;
    isPrepaidToPostpaid?: boolean;
    isServiceSubscription?: boolean;
    keepExistingSubscription?: boolean;
    lineThrough?: string;
    maxAgeAllowed?: number;
    name: string;
    oldSubscription?: OldSubscription;
    phoneNumber?: ICartSubscriptionPhoneNumber;
    price?: ICartSubscriptionPrice;
    productUser?: ICartSubscriptionProductUser;
    reswitch?: ICartSubscriptionReswitch;
    size: MaybeEmpty<ICartSize>;
    type?: ICartType;
    url?: string;
}

export interface ICartItemQuantity {
    modifiable?: boolean;
    removable?: boolean;
    value: number;
}

export interface ICartItemPrice {
    firstInvoice?: number;
    monthly?: number;
    monthlyWithoutVAT?: number;
    originalSalesPrice?: number;
    originalSalesPriceWithoutVAT?: number;
    total?: number;
    upfront?: number;
    upfrontWithoutVAT?: number;
}

interface ICartItemDiscountValue {
    monthly: number;
    upfront: number;
}

export interface ICartItemDiscount {
    description?: string;
    discountSubscriptionType?: string[];
    isManualDiscount?: boolean;
    isSmartDiscount?: boolean;
    name?: string;
    smartDiscountCode?: string;
    type?: DiscountType;
    types?: CartDiscountType[];
    value: ICartItemDiscountValue;
}

interface ICartItemImageIcon {
    icon?: IconName;
}

interface ICartItemImageUrl {
    url: string;
}

type ICartItemImage = ICartItemImageIcon | ICartItemImageUrl;

type SubscriptionStatus = {
    isActive?: boolean;
    isDraft?: boolean;
};

export interface ICartSubtitleItem {
    description: string;
    priceInfo?: string;
}

export interface ICartItemBadge {
    variant: BadgeVariant;
    text: string;
}

export interface ICartItemBase {
    badges?: ICartItemBadge[];
    brand?: string;
    brandSlug?: string;
    bundleId?: string;
    cartItemId?: string;
    code?: string;
    color?: string;
    depth?: number;
    discount?: ICartItemDiscount;
    ean?: string;
    href?: string;
    image?: ICartItemImage;
    imei?: string;
    indent?: boolean;
    isLegal?: boolean;
    isReSwitch?: boolean;
    isWebDeal?: boolean;
    items?: ICartItem[];
    leaseMonths?: string | number;
    lineThrough?: string;
    model: string;
    modelSize?: string;
    modelSlug?: string;
    name: string;
    onlyAvailableInShops?: boolean;
    parentId?: string;
    price: ICartItemPrice;
    quantity: ICartItemQuantity;
    rankedSimCardTypes?: RankedSimCardType[];
    subtitle?: (string | ICartSubtitleItem)[];
    subtype?: string;
    supportedEsimTypes?: EsimType[];
}

export interface ICartItemInsurance extends ICartItemBase {
    id: InsuranceID;
    type: "SERVICE";
}
export interface ICartItemDelivery extends ICartItemBase {
    id: DeliveryType;
    type: "DELIVERY";
}

export interface ICartItemSubscription extends PartialBy<ICartItemBase, "model"> {
    id: OfferingCode;
    status: SubscriptionStatus;
    type: ICartSubscriptionType;
}

export type ICartItemOtherType = Exclude<ICartType, "DELIVERY" | ICartSubscriptionType>;

export interface ICartItemOther extends ICartItemBase {
    id: string;
    isEsim?: boolean;
    isExistingSim?: boolean;
    type: ICartItemOtherType;
}

export type ICartItem = ICartItemInsurance | ICartItemDelivery | ICartItemSubscription | ICartItemOther;
export type ICartItemWithBundleId = WithRequired<ICartItem, "bundleId">;

export interface ICartDeliveryOption {
    label?: string;
    price?: number;
    pricePrefix?: string;
    type?: string;
}

export interface DisclaimerItem {
    description: string | string[];
    leaseMonths?: number | string;
    name: string;
    total: number;
}

export type FlexibleLeaseType = "partlyUpfront" | "allUpfront";
